import { useEffect, useRef, useState } from 'react';

import { formatISO } from 'date-fns';
import { Toast } from 'primereact/toast';

import { useAppDispatch } from '@/app/hooks';
import {
  inClinicApi,
  useImplantEmbryoMutation,
} from '@/app/services/inClinic';
import { EmbryoLink } from '@/components/common/links';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import type { ImplantSession } from '../../hooks/useImplantSession';
import type { DataEntryRecord } from '../../types';
import { getFriendlyError } from '../../utils/errorMessages';
import { BillingAccountSearchDialog } from './BillingAccountSearchDialog';

type ImplantEmbryoDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: DataEntryRecord;
  cl: string;
  session: ImplantSession;
  canWrite: boolean;
  thawMethod: string;
  usePartnership: boolean;
  billingAccountId: string;
  billingAccountName: string;
  onSuccess: () => void;
};

const RECIP_TYPE_OPTIONS = [
  { label: 'Live Calf', value: 'Live Calf' },
  { label: 'Purchased Pregnancy', value: 'Purchased Pregnancy' },
  { label: 'Client', value: 'Client' },
  { label: 'Clone', value: 'Clone' },
  { label: 'Research', value: 'Research' },
  { label: 'Training', value: 'Training' },
  { label: 'Weaned Calf', value: 'Weaned Calf' },
];

export function ImplantEmbryoDialog({
  open,
  onOpenChange,
  entry,
  cl,
  session,
  canWrite,
  thawMethod,
  usePartnership,
  billingAccountId: initialBillingAccountId,
  billingAccountName: initialBillingAccountName,
  onSuccess,
}: ImplantEmbryoDialogProps) {
  const isFrozen = !entry.isFresh;
  const linkToEmbryoModule = useFeatureFlag('Link_To_Embryo_Module');
  const useReadableErrors = useFeatureFlag('Replace_tech_error_with_readable');
  const allowRecipTypeChange = useFeatureFlag(
    'Implant_Allow_Recip_Implant_Type_Change',
  );
  const caneSearchFix = useFeatureFlag('Implant_Cane_Search_Fix');
  const syncImplantDate = useFeatureFlag('In_Clinic_Date_Sync');
  const dispatch = useAppDispatch();
  const showRecipType = isFrozen || allowRecipTypeChange;
  const [transQual, setTransQual] = useState(entry.transQual || 1);
  const [transDist, setTransDist] = useState(entry.transDist || 7);
  const [comment, setComment] = useState('');
  const [hcgApplied, setHcgApplied] = useState(false);
  const [keepInQueue, setKeepInQueue] = useState(false);
  const [secEmbryo, setSecEmbryo] = useState(false);
  const [recipType, setRecipType] = useState(entry.recipType || '');
  const [embryoTagNumber, setEmbryoTagNumber] = useState(
    entry.embryoTagNumber || '',
  );
  const [billingAccountId, setBillingAccountId] = useState(
    initialBillingAccountId,
  );
  const [billingAccountName, setBillingAccountName] = useState(
    initialBillingAccountName,
  );
  const [showBillingSearch, setShowBillingSearch] = useState(false);
  const errorToast = useRef<Toast>(null);

  const [implantEmbryo, { isLoading }] = useImplantEmbryoMutation();

  useEffect(() => {
    if (open) {
      setTransQual(entry.transQual || 1);
      setTransDist(entry.transDist || 7);
      setRecipType(entry.recipType || '');
      setEmbryoTagNumber(entry.embryoTagNumber || '');
      setBillingAccountId(initialBillingAccountId);
      setBillingAccountName(initialBillingAccountName);
    }
  }, [open, entry, initialBillingAccountId, initialBillingAccountName]);

  const handleSubmit = async () => {
    if (showRecipType && !recipType) {
      errorToast.current?.show({
        severity: 'warn',
        summary: 'Recip Type required',
        detail: 'Embryos require a recipient type',
        life: 3000,
      });
      return;
    }
    if (!billingAccountId) {
      errorToast.current?.show({
        severity: 'warn',
        summary: 'Billing Account required',
        detail: 'Please select a Billing Account before saving',
        life: 3000,
      });
      return;
    }
    const result = await implantEmbryo({
      embryoId: entry.embryoId,
      recipId: entry.haulIn ? null : entry.recipId,
      recipQueueId: entry.recipQueueId,
      yardId: session.yardId,
      siteId: session.siteId,
      vetId: session.vetId,
      techId: session.techId,
      cl,
      transQual,
      transDist,
      comment,
      hcgApplied,
      keepInQueue,
      secEmbryo,
      usePartnership,
      haulIn: entry.haulIn,
      recipName: entry.haulIn ? entry.recipName : null,
      embryoOwnerId: entry.embryoOwnerId,
      impDate: syncImplantDate ? formatISO(new Date()) : session.impDate,
      chuteNumber: session.chuteNumber,
      recipSync: entry.recipSync || null,
      recipType: showRecipType ? recipType : null,
      thawMethod: isFrozen && thawMethod ? thawMethod : null,
      embryoTagNumber: embryoTagNumber || null,
      billingAccountId: billingAccountId || null,
    });
    if ('error' in result) {
      errorToast.current?.show({
        severity: 'error',
        summary: 'Implant failed',
        detail: getFriendlyError(
          result.error as { status: number | string; data?: string },
          'Implant',
          useReadableErrors,
        ),
        life: 4000,
      });
      return;
    }
    if (caneSearchFix) {
      dispatch(
        inClinicApi.util.invalidateTags(['InClinicFrozenEmbryos']),
      );
    }
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="md:max-w-2xl"
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            !isLoading &&
            (e.target as HTMLElement).tagName !== 'INPUT' &&
            (e.target as HTMLElement).tagName !== 'TEXTAREA'
          ) {
            e.preventDefault();
            handleSubmit();
          }
        }}
      >
        <Toast ref={errorToast} />
        <DialogHeader>
          <DialogTitle>Implant Embryo</DialogTitle>
          {linkToEmbryoModule && entry.mating && (
            <div className="text-muted-foreground text-sm">
              <EmbryoLink
                embryoId={entry.embryoId}
                embryoName={entry.mating}
                isOutside={entry.isOutsideEmbryo}
                openInNewTab
              />
            </div>
          )}
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Transfer Quality</span>
              <Input
                type="number"
                value={transQual}
                onChange={(e) => setTransQual(Number(e.target.value))}
                placeholder="Quality"
                inputMode="numeric"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Transfer Distance</span>
              <Input
                type="number"
                value={transDist}
                onChange={(e) => setTransDist(Number(e.target.value))}
                placeholder="Distance"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Embryo Tag #</span>
            <Input
              value={embryoTagNumber}
              onChange={(e) => setEmbryoTagNumber(e.target.value)}
              placeholder="Tag number..."
              inputMode="numeric"
            />
          </div>

          {showRecipType && (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">
                Recip Type <span className="text-red-500">*</span>
              </span>
              <Select
                value={recipType}
                onChange={(val) => setRecipType(String(val))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {RECIP_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Comment</span>
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Optional comment..."
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              Implant Billing Account <span className="text-red-500">*</span>
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm">
                {billingAccountName || (
                  <span className="text-muted-foreground italic">
                    None selected
                  </span>
                )}
              </span>
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => setShowBillingSearch(true)}
              >
                Search…
              </Button>
              {billingAccountId && (
                <Button
                  type="button"
                  size="xs"
                  variant="ghost"
                  onClick={() => {
                    setBillingAccountId('');
                    setBillingAccountName('');
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={hcgApplied}
                onCheckedChange={(checked) => setHcgApplied(!!checked)}
              />
              HCG Applied
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={keepInQueue}
                onCheckedChange={(checked) => setKeepInQueue(!!checked)}
              />
              Keep in Queue (2nd Embryo)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={secEmbryo}
                onCheckedChange={(checked) => setSecEmbryo(!!checked)}
              />
              Second Embryo
            </label>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !canWrite}>
            {isLoading ? 'Implanting...' : 'Implant'}
          </Button>
        </DialogFooter>
      </DialogContent>
      <BillingAccountSearchDialog
        open={showBillingSearch}
        onOpenChange={setShowBillingSearch}
        onSelect={(id, name) => {
          setBillingAccountId(id);
          setBillingAccountName(name);
        }}
      />
    </Dialog>
  );
}
